import { CommonModule } from '@angular/common';
import { BookSessionPaymentDialogComponent } from '../book-session-payment-dialog/book-session-payment-dialog.component';
import { Component, Inject, NgZone, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MentorReviewConfirmDialogComponent } from '../mentor-review-confirm-dialog/mentor-review-confirm-dialog.component';
import { catchError, finalize, of, Subscription, tap } from 'rxjs';
import { MentorService } from '@edtech-ui/core/services/mentor-management/mentor.service';
import { ToastrService } from 'ngx-toastr';
import { MentorBookSessionDialogComponent } from '../mentor-book-session-dialog/mentor-book-session-dialog.component';
import { MentorSessionBookingPayload } from '@edtech-ui/core/models/mentors-sessions/post-confirm-reserve-slots';
import { PaymentService } from '@edtech-ui/core/services/payment-management/payment.service';
import { RazorPaymentPayload } from '@edtech-ui/core/models/mentors-sessions/create-order-razorpay';
import { RazorPaymentVerifyPayload } from '@edtech-ui/core/models/mentors-sessions/post-razor-payment-verify';
import { MentorBookingPayload } from '@edtech-ui/core/models/mentors-sessions/post-booking-session';
import { MentorSessionFreePayment } from '@edtech-ui/constants/mentor-session-free-payment.enum';
import { environment } from 'environments/environment';
import { HasPermissionDirective } from '@edtech-ui/access-role-permissions/users-permissions-directives/has-permissions.directives';
import { PermissionForStudents } from '@edtech-ui/access-role-permissions/users-permissions-constants/student-roles-permissions.enum';
import { Router } from '@angular/router';
import { formatTimeBasedOnTimezone } from '@edtech-ui/core/utils/date-time.util';
declare var Razorpay: any;

@Component({
	selector: 'app-mentor-review-booking-dialog',
	standalone: true,
	imports: [CommonModule, HasPermissionDirective],
	templateUrl: './mentor-review-booking-dialog.component.html',
	styleUrl: './mentor-review-booking-dialog.component.scss'
})
export class MentorReviewBookingDialogComponent implements OnDestroy, OnInit {
	isLoading = false;
	isPaymentProcessing = false;
	subscription = new Subscription();
	MentorSessionFreePayment = MentorSessionFreePayment;
	studentName = '';
	remainingSeconds = 0;
	countdownInterval: any;
	mentorBookingId = '';
	mentorEmail: string | null = null;
	PermissionForStudents = PermissionForStudents;

	constructor(
		private dialogRef: MatDialogRef<MentorReviewBookingDialogComponent>,
		private dialog: MatDialog,
		private _mentorService: MentorService,
		private _toastr: ToastrService,
		private _paymentService: PaymentService,
		private ngZone: NgZone,
		private router: Router,
		@Inject(MAT_DIALOG_DATA) public payload: MentorSessionBookingPayload & {
			first_name: string; last_name: string; designation: string; appointment_type_name: string;
			company: string; price_display: string; formState?: any; pricing_id: string; product_id: string; display: string;
			expires_in_seconds: number; booking_id: string;
		}
	) { }

	ngOnInit(): void {
		this.studentName = this._mentorService.studentName.getValue();
		this.mentorBookingId = this.payload.booking_id;
		this.remainingSeconds = this.payload.expires_in_seconds;
		this.startCountdown();
		this.mentorEmail = localStorage.getItem('email');

		if (!this.payload.display && this.payload.start_time) {
			this.payload.display = formatTimeBasedOnTimezone(this.payload.start_time);
		}
	}

	close() {
		this.dialogRef.close();
	}

	openPaymentDialogBox(response: any) {
		this.dialog.open(BookSessionPaymentDialogComponent, {
			disableClose: true,
			data: {
				razorPayResponse: response
			}
		});
		this.close();
	}

	OpenReviewBookingForm() {
		this.dialog.open(MentorReviewConfirmDialogComponent, {
			panelClass: 'review-booking-dialog',
			disableClose: true,
		});
	}

	back() {
		this.dialogRef.afterClosed().subscribe(() => {
			this.dialog.open(MentorBookSessionDialogComponent, {
				data: {
					mentor_id: this.payload.mentor_id,
					first_name: this.payload.first_name,
					last_name: this.payload.last_name,
					designation: this.payload.designation,
					price_display: this.payload.price_display,
					formState: this.payload.formState
				}
			});
		});
		this.close()
	}


	postRazorPayment() {
		const paylod: RazorPaymentPayload = {
			product_id: this.payload.product_id,
			pricing_id: this.payload.pricing_id,
			quantity: 1,
			booking_id: this.mentorBookingId,
			mentor_id: this.payload.mentor_id
		}
		this.isLoading = true;
		this.subscription.add(
			this._paymentService.postCreateOrderRazorPayment(paylod).pipe(
				tap(response => {
					if (response.Success) {
						this.isPaymentProcessing = true;
						this._paymentService.orderId.next(response.Data.order_id);
						this._paymentService.razorpayOrderId.next(response.Data.razorpay_order_id);
						this.openRazorPayDialogBox(response.Data.razorpay_order_id);
						clearInterval(this.countdownInterval);
						this.dialogRef.close({ keepAlive: true });
					} else {
						this._toastr.error(response.Message);
					}
				}),
				finalize(() => {
					this.isLoading = false;
				}),
				catchError(() => {
					this._toastr.error('Something went wrong. Please try again');
					return of(null);
				}),
			).subscribe()
		)
	}

	openRazorPayDialogBox(currentRazorpayOrderId: string) {
		const options = {
			key: environment.razorPayKey,
			amount: Number(this.payload.price_display) * 100,
			currency: 'INR',
			name: `${this.studentName}`,
			description: 'Mentor Session Payment',
			order_id: currentRazorpayOrderId,
			prefill: {
				name: this.studentName,
				email: this.mentorEmail,
			},
			theme: { color: '#3D348B' },
			handler: (response: any) => {
				this.ngZone.run(() => {
					this.postPaymentVerify(response);
				});
			}
		}

		const razorpay = new Razorpay(options);

		razorpay.on('payment.failed', (error: any) => {
			this._toastr.error('Payment failed. Please try again.');
		});
		razorpay.open();

	}


	postPaymentVerify(response: any) {

		const currentOrderId = this._paymentService.orderId.getValue();
		const currentRazorpayOrderId = this._paymentService.razorpayOrderId.getValue();

		const payload: RazorPaymentVerifyPayload = {
			razorpay_order_id: currentRazorpayOrderId,
			razorpay_payment_id: response.razorpay_payment_id,
			razorpay_signature: response.razorpay_signature,
			order_id: currentOrderId
		}

		this.subscription.add(
			this._paymentService.postRazorPaymentVerify(payload).pipe(

				tap(response => {
					if (response.Success) {
						this._paymentService.razorPayPaymentId.next(response.Data.razorpay_payment_id);
						this.postConfirmBookingSessionForm();
						this._toastr.success(response.Message);
					} else {
						this._toastr.error(response.Message);
					}
				}),
				catchError(() => {
					this._toastr.error('Something went wrong');
					return of(null);
				})
			).subscribe()
		)
	}

	postConfirmBookingSessionForm() {

		const bookingId = this.mentorBookingId;
		const orderId = this._paymentService.orderId.getValue();

		const payloadTest: MentorBookingPayload = {
			booking_id: bookingId,
			order_id: orderId,
			mentor_id: this.payload.mentor_id
		}

		this.subscription.add(
			this._mentorService.postMentorSession(payloadTest).pipe(
				tap(response => {
					if (response.Success) {
						this.isPaymentProcessing = false;
						this._mentorService.saveConfirmBookingData.next(response.Data);
						this.OpenReviewBookingForm();
						this.subscription.unsubscribe();
						this.dialogRef.close();
					} else {
						this._toastr.error(response.Message);
					}
				}),
				catchError(() => {
					this._toastr.error('Something went wrong while booking session form');
					return of(null);
				})
			).subscribe());
	}

	startCountdown() {
		this.countdownInterval = setInterval(() => {
			if (this.remainingSeconds > 0) {
				this.remainingSeconds--;
			} else {
				this.dialogRef.close();
			}
		}, 1000);
	}

	formatTime(seconds: number): string {
		const min = Math.floor(seconds / 60);
		const sec = seconds % 60;
		return `${min}:${sec < 10 ? '0' + sec : sec}`;
	}

	ngOnDestroy(): void {
		clearInterval(this.countdownInterval);
		if (!this.isPaymentProcessing) {
			this.subscription.unsubscribe();
		}
	}
}